import type { ColumnCatalogDef } from '@/types/column-catalog'
import { SHARED_COLUMNS } from '@/column-registry/shared-columns'
import type { ColumnType } from '@/features/sliced-form/schemas/column-compatibility'
import type { EnumOption } from '@/column-registry/enum-options'

type DataType = ColumnCatalogDef['dataType']

export interface ColumnDefOpts {
  group?: string
  enumOptions?: EnumOption[]
  formLabel?: string
  isPremarket?: boolean
  isAfterhours?: boolean
  isHistorical?: boolean
  allowNull?: boolean
  timeSliceable?: boolean
  keywords?: string[]
  /**
   * Measurement-domain bucket override (legacy `columnType`). When omitted,
   * the factory derives a default from `dataType` via `defaultColumnType`.
   * Pass this explicitly when the dataType-based default would be wrong —
   * e.g. `dollar_vol` is `dataType: 'volume'` but semantically belongs in
   * `largePrice`, and `total_news_count` is `dataType: 'number'` but should
   * be `arbitraryNumber` (incomparable by default to other counts).
   */
  columnType?: ColumnType
  /** Legacy `comparesTo` override. See `ColumnCatalogDef.comparesTo`. */
  comparesTo?: string[]
  /**
   * Grid-only column that must not be offered as a filter criterion (no
   * backend field to filter on) — e.g. `custom_rvol` and the key-stats
   * client-computed derived financials. See `ColumnCatalogDef.displayOnly`.
   */
  displayOnly?: boolean
  /**
   * Max values selectable for this enum column in the filter builder's
   * multi-select. Used by the `watchlists` filter to cap how many watchlists a
   * single scan references (bounds the inlined ticker literals the backend
   * generates). See `ColumnCatalogDef.maxFilterSelections`.
   */
  maxFilterSelections?: number
}

/**
 * Derive a sensible `columnType` from `dataType` when no explicit tag is
 * provided. The catch-all `number` deliberately maps to `arbitraryNumber`
 * (default-incomparable) so misc-counts like `rvol` / `total_news_count` /
 * `pr_count` never silently pair with each other; columns that are actually
 * discrete-count comparators (e.g. `trades`, `sharesoutstanding`) must opt
 * in via an explicit `largeInteger` tag.
 */
export function defaultColumnType(dataType: DataType): ColumnType {
  switch (dataType) {
    case 'currency':
      return 'price'
    case 'percent':
      return 'percentage'
    case 'volume':
      return 'volume'
    case 'number':
      return 'arbitraryNumber'
    case 'string':
      return 'string'
    case 'enum':
      return 'enum'
    case 'boolean':
      return 'boolean'
    case 'date':
      return 'date'
  }
}

/**
 * True for "position in range" percent columns — the value is bounded [0,100]
 * (0 = at the range low, 100 = at the range high, per the backtest
 * `positionInRange` contract). These are the scanner `POSITION_IN_RANGE` folder
 * and history `'Position in Range'` group fields; both share the key suffix
 * `range_pct` / `range_close_pct` (e.g. `range_pct`, `pm_range_pct`,
 * `day_5_range_pct`, `day0_range_close_pct`). Gates the grid's `bullet` display
 * mode. Distinct from relative-to-high/low percents (`_high_pct`, `_low_pct`),
 * which are NOT flagged.
 */
export function isPositionInRangeKey(key: string): boolean {
  return key === 'range_pct' || key.endsWith('_range_pct') || key.endsWith('_range_close_pct')
}

/**
 * Shared column definition builder used by scanner, calendar, and other
 * feature-specific column catalogs. Looks up `SHARED_COLUMNS` for label/type
 * overrides, then spreads any feature-specific options. The resulting
 * `columnType` precedence is: shared override → opts override → dataType default.
 */
export function createColumnDef(
  key: string,
  label: string,
  dataType: DataType,
  category: string,
  opts?: ColumnDefOpts,
): ColumnCatalogDef {
  const shared = SHARED_COLUMNS[key]
  const resolvedDataType = shared?.dataType ?? dataType
  const columnType = shared?.columnType ?? opts?.columnType ?? defaultColumnType(resolvedDataType)
  const comparesTo = shared?.comparesTo ?? opts?.comparesTo
  // Per-call opts win over shared meta so a feature catalog can still
  // override (e.g. point country at a different option list). Default
  // is whatever the shared meta carries.
  const enumOptions = opts?.enumOptions ?? shared?.enumOptions
  return {
    key,
    label: shared?.label ?? label,
    dataType: resolvedDataType,
    category,
    columnType,
    ...(resolvedDataType === 'percent' && isPositionInRangeKey(key)
      ? { isRangePercent: true }
      : {}),
    ...(comparesTo != null ? { comparesTo } : {}),
    ...(opts?.group != null ? { group: opts.group } : {}),
    ...(enumOptions != null ? { enumOptions } : {}),
    ...(opts?.formLabel != null ? { formLabel: opts.formLabel } : {}),
    ...(opts?.isPremarket ? { isPremarket: true } : {}),
    ...(opts?.isAfterhours ? { isAfterhours: true } : {}),
    ...(opts?.isHistorical ? { isHistorical: true } : {}),
    ...((opts?.allowNull ?? shared?.allowNull) ? { allowNull: true } : {}),
    ...(opts?.timeSliceable ? { timeSliceable: true } : {}),
    ...(opts?.keywords?.length ? { keywords: opts.keywords } : {}),
    ...(opts?.displayOnly ? { displayOnly: true } : {}),
    ...(opts?.maxFilterSelections != null ? { maxFilterSelections: opts.maxFilterSelections } : {}),
  }
}
